import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BudgetCategory } from '../models/budget-category.model';
import { BehaviorSubject, debounce, debounceTime, distinctUntilChanged, filter, finalize, Observable, Subject, switchMap, tap } from 'rxjs';
import { FilterBudgetGroupItem } from '../dtos/filter-budget-group-item.dto';
import { GrupoItemResponse } from '../dtos/grupo-itens-response.model';
import { GruposItensDto } from '../dtos/grupos-itens.dto';
import { GrupoItemorcamentoResponse } from '../dtos/obter-itens-grupo-response.dto';
import { AdicionarItemGrupoDto } from '../dtos/adicionar-item-grupo.dto';
import { AtualizarItemGrupoDto } from '../dtos/atualizar-item-grupo.dto';
import { ItemGrupoOrdemDto } from '../models/item-grupo-ordem.dto';

@Injectable({
  providedIn: 'root'
})
export class BudgetService {
  private _apiUrl = environment.apiUrl + "/v1/GrupoItemOrcamento";

  // BehaviorSubject para armazenar a lista de grupos
  private gruposItensSubject = new BehaviorSubject<GruposItensDto | null>(null);

  // Observable público para os componentes se inscreverem
  grupos$ = this.gruposItensSubject.asObservable();

  private groupItemsSubject = new BehaviorSubject<GrupoItemResponse | null>(null);
  groupItems$ = this.groupItemsSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(true);

  private filterSubject = new BehaviorSubject<FilterBudgetGroupItem>({
    "PaginaAtual": 1,
    "QuantidadePorPagina": 10,
    Nome: "",
    GrupoId: "",
  })

  setFilters(filters: Partial<FilterBudgetGroupItem>): void {
    const currentFilters = this.filterSubject.value;
    this.filterSubject.next({ ...currentFilters, ...filters });
  }

  constructor(private http: HttpClient) { }

  // INCLUI NOVO ITEM NA LISTA/TABELA DE ORÇAMENTO
  incluirItemGrupo(adicionarItemGrupoDto: AdicionarItemGrupoDto) {

    return this.http.post(`${this._apiUrl}/itens`, adicionarItemGrupoDto).pipe(
      tap(() => {
        // Atualiza a lista apenas se a requisição for bem-sucedida
        this.atualizarLista(adicionarItemGrupoDto.orcamentoId!);
      })
    );
  }

  /**
   * ATUALIZA ITEM DA LISTA/TABELA DE ORÇAMENTO
   * @param atualizarItemGrupoDto 
   * @returns 
   */
  atualizarItemGrupo(atualizarItemGrupoDto: AtualizarItemGrupoDto, sobrepor: boolean) {

    const payload = {
      orcamentoId: atualizarItemGrupoDto.orcamentoId,
      quantidade: atualizarItemGrupoDto.quantidade,
      quantidadeDePessoa: atualizarItemGrupoDto.quantidadeDePessoa,
      quantidadeDeDia: atualizarItemGrupoDto.quantidadeDeDia,
      valorUnitario: atualizarItemGrupoDto.valorUnitario,
      tipoDeCalculo: atualizarItemGrupoDto.tipoDeCalculo,
      sobrepor: sobrepor ? 'true' : 'false'
    }

    return this.http.put(`${this._apiUrl}/itens/${atualizarItemGrupoDto.id}`, payload).pipe(
      tap(() => {
        // Atualiza a lista apenas se a requisição for bem-sucedida
        this.atualizarLista(atualizarItemGrupoDto.orcamentoId!);
      })
    );
  }

  // EXCLUI ITEM DA LISTA/TABELA DE ORÇAMENTO
  excluirItemGrupo(id: string, orcamentoId: string) {

    return this.http.delete(`${this._apiUrl}/itens/${id}`).pipe(
      tap(() => {
        // Atualiza a lista apenas se a requisição for bem-sucedida
        this.atualizarLista(orcamentoId);
      })
    );
  }

  registerBudgetcategory(budgetCategory: BudgetCategory) {

    let payloas: BudgetCategory = {
      nome: budgetCategory.nome,
      apresentarEmOrcamento: budgetCategory.apresentarEmOrcamento == undefined ? false : budgetCategory.apresentarEmOrcamento,
      tipoDeCalculo: budgetCategory.tipoDeCalculo,
      tipoDeApresentacao: budgetCategory.tipoDeApresentacao,
      tipoDeDespesa: budgetCategory.tipoDeDespesa
    }

    return this.http.post(this._apiUrl, payloas);
  }

  updateBudgetcategory(budgetCategory: BudgetCategory): Observable<BudgetCategory> {
    return this.http.put<BudgetCategory>(`${this._apiUrl}/${budgetCategory.id}`, budgetCategory);
  }


  getGroups(): Observable<BudgetCategory[]> {

    this.loadingSubject.next(true);

    return this.http.get<BudgetCategory[]>(this._apiUrl)
      .pipe(
        finalize(() => this.loadingSubject.next(false))
      );
  }

  updateGroups(groups: BudgetCategory[]): Observable<BudgetCategory> {

    const payload = { grupos: groups };

    return this.http.put<BudgetCategory>(`${this._apiUrl}/parametros`, payload);
  }

  getCategoryById(id: string): Observable<BudgetCategory> {
    return this.http.get<BudgetCategory>(`${this._apiUrl}/${id}`);
  }

  deleteCategory(id: string): Observable<any> {
    return this.http.delete(`${this._apiUrl}/${id}`)
      .pipe(
        finalize(() => this.loadingSubject.next(false))
      );
  }

  getGroupsItens(id: string): Observable<GrupoItemResponse> {
    // Pega os filtros atuais do filterSubject
    const currentFilters = this.filterSubject.value;
    
    // Constrói os parâmetros HTTP diretamente, apenas com os campos necessários
    let params = new HttpParams();
    
    // Sempre adiciona GrupoId se fornecido
    if (id && id.length > 0) {
      params = params.append("GrupoId", id);
    }
    
    // Adiciona PaginaAtual (sempre presente)
    if (currentFilters.PaginaAtual) {
      params = params.append("PaginaAtual", currentFilters.PaginaAtual.toString());
    } else {
      params = params.append("PaginaAtual", "1");
    }
    
    // Adiciona QuantidadePorPagina (sempre presente)
    if (currentFilters.QuantidadePorPagina) {
      params = params.append("QuantidadePorPagina", currentFilters.QuantidadePorPagina.toString());
    } else {
      params = params.append("QuantidadePorPagina", "10");
    }
    
    // Adiciona Nome apenas se não estiver vazio
    if (currentFilters.Nome && currentFilters.Nome.trim() !== "") {
      params = params.append("Nome", currentFilters.Nome);
    }

    this.loadingSubject.next(true);

    // Faz a requisição diretamente sem observar o filterSubject
    return this.http
      .get<GrupoItemResponse>(`${this._apiUrl}/itens`, { params })
      .pipe(
        finalize(() => this.loadingSubject.next(false))
      );
  }

  consultarGruposItens(orcamentoId: string) {
    return this.http.get<GruposItensDto>(`${this._apiUrl}/${orcamentoId}/grupos-itens`).subscribe({
      next: (grupos: GruposItensDto) => {
        this.gruposItensSubject.next(grupos);

        this.loadingSubject.next(false)
      }
    });
  }

  // Método para notificar os componentes que a lista foi atualizada
  atualizarLista(orcamentoId: string): void {
    this.consultarGruposItens(orcamentoId); // Recarrega os grupos da API e atualiza o BehaviorSubject
  }

  // Método para se inscrever nas notificações de grupo criado
  // onGrupoCriado(): Observable<void> {
  //   return this.gruposItensDto.asObservable();
  // }

  //CONSULTAR GRUPO POR ID E ITENS VINCULADOS AO GRUOPS
  consultarGrupoItensGrupoId(grupoId: string): Observable<GrupoItemorcamentoResponse> {
    const params = new HttpParams().set('grupoId', grupoId);

    return this.http.get<GrupoItemorcamentoResponse>(`${this._apiUrl}/itens-para-grupos`, { params })
  }

  public get loading() {
    return this.loadingSubject.value;
  }

  get filters(): FilterBudgetGroupItem {
    return this.filterSubject.value;
  }


  atualizarOrdem(grupos: ItemGrupoOrdemDto[]): Observable<string> {

    const payload = { grupos: grupos };

    console.log('PAYLAOD DE ENVIO ', JSON.stringify(payload));

    return this.http.put<string>(`${this._apiUrl}/itens/ordem`, payload).pipe(
      // tap(() => {
      //   // Atualiza a lista apenas se a requisição for bem-sucedida
      //   this.gruposItensSubject.next(payload);
      // })
    );
  }

  baixarExcelOrcamento(orcamentoId: string): Observable<Blob> {
    const url = `${this._apiUrl}/${orcamentoId}/download-itens-orcamento`;

    return this.http.get(url, {
      responseType: 'blob'
    });
  }
}
